import { useEffect, useState } from 'react';
import { api, ConfigDiff, ConfigSnapshot } from '../api';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

export default function Snapshots({ session }: Props) {
  const [snaps, setSnaps] = useState<ConfigSnapshot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [a, setA] = useState<string>('');
  const [b, setB] = useState<string>('');
  const [diff, setDiff] = useState<ConfigDiff[] | null>(null);

  useEffect(() => {
    api.listSnapshots(session.gameId).then(setSnaps).catch(e => setError(e.message));
  }, [session.gameId]);

  const computeDiff = async () => {
    if (!a || !b) return;
    try {
      const d = await api.diffSnapshots(session.gameId, a, b);
      setDiff(d);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const rollback = async (id: string) => {
    if (!confirm(`Rollback to snapshot ${id}?`)) return;
    try {
      await api.rollback(session.gameId, id, session.userId);
      api.listSnapshots(session.gameId).then(setSnaps);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Container>
      <PageHeader
        title="Snapshots"
        subtitle="Every publish creates a frozen snapshot. Diff or roll back to any prior version."
      />

      {error && <div className="error-banner">{error}</div>}

      <div className="card mb-5">
        <h3 className="m-0 mb-3 text-sm font-semibold">Compare snapshots</h3>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">
              From
            </label>
            <select className="input" value={a} onChange={e => setA(e.target.value)}>
              <option value="">— select —</option>
              {snaps.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">
              To
            </label>
            <select className="input" value={b} onChange={e => setB(e.target.value)}>
              <option value="">— select —</option>
              {snaps.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" onClick={computeDiff} disabled={!a || !b}>
            Show diff
          </button>
        </div>
        {diff && (
          <pre className="diff">{JSON.stringify(diff, null, 2)}</pre>
        )}
      </div>

      <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">
        All snapshots
      </h2>
      {snaps.length === 0 ? (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No snapshots yet</div>
          <div className="text-xs text-fg-muted">Publish a draft to create the first snapshot.</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>When</th>
                <th>Notes</th>
                <th>State</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {snaps.map(s => (
                <tr key={s.id}>
                  <td>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-[11px] text-fg-muted font-mono mt-0.5">{s.id}</div>
                  </td>
                  <td className="text-xs text-fg-muted">
                    {new Date(s.created_at).toLocaleString()}
                  </td>
                  <td>{s.notes || <span className="text-fg-muted italic">—</span>}</td>
                  <td>
                    {s.is_current ? (
                      <span className="badge badge-current">current</span>
                    ) : (
                      <span className="badge badge-muted">archived</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn"
                      disabled={s.is_current}
                      onClick={() => rollback(s.id)}
                    >
                      Rollback
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Container>
  );
}
